import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
    const nav = useNavigate();
    const { login, loading } = useAuth();
    const [email, setEmail] = useState('admin@dharmikgardens.com');
    const [password, setPassword] = useState('admin123');
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(email, password);
            toast.success('Welcome back!');
            nav('/dharmikdashboard');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Login failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="login-hero-bg text-white p-8 sm:p-12 flex flex-col justify-between relative overflow-hidden">
                <div className="relative z-10">
                    <Logo size={48} />
                </div>
                <div className="relative z-10">
                    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                        Dharmik Gardens<br />
                        <span className="text-brand-green-400" style={{ color: 'hsl(142 76% 70%)' }}>Admin Console</span>
                    </h1>
                    <p className="mt-4 text-white/80 max-w-md">
                        Manage your nursery business — from catalog and inventory to orders, wholesale customers, and service enquiries — all in one place.
                    </p>
                    <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
                        {[
                            'Multi-tier B2B/B2C pricing',
                            'Shopify-style order tracking',
                            'State-wise shipping',
                            'Role-based access control',
                        ].map((f) => (
                            <div key={f} className="flex items-start gap-2 text-sm">
                                <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'hsl(142 76% 70%)' }} />
                                <span className="text-white/90">{f}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="relative z-10 text-xs text-white/60">© {new Date().getFullYear()} Dharmik Gardens Private Limited</div>
            </div>

            <div className="flex items-center justify-center p-6 sm:p-12 bg-neutral-50">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <h2 className="text-2xl font-semibold tracking-tight">Sign in to your account</h2>
                        <p className="text-sm text-neutral-600 mt-1">Use your admin credentials to access the dashboard.</p>
                    </div>
                    <form onSubmit={submit} className="space-y-4 bg-white p-6 rounded-xl border shadow-sm" data-testid="login-form">
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="login-email-input" />
                        </div>
                        <div>
                            <Label htmlFor="password">Password</Label>
                            <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="login-password-input" />
                        </div>
                        <Button type="submit" className="w-full" disabled={submitting} data-testid="login-submit-button"
                            style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Sign In
                        </Button>
                    </form>
                    <div className="mt-6 p-4 rounded-lg border bg-blue-50 text-xs text-blue-900 space-y-1">
                        <div className="font-semibold">Demo credentials</div>
                        <div>Super Admin — admin@dharmikgardens.com / admin123</div>
                        <div>Sales — sales@dharmikgardens.com / sales123</div>
                        <div>Inventory Manager — inventory@dharmikgardens.com / inventory123</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
